import { memo, useMemo } from "react";
import { useDraftFlowData, useDraftFlowActions } from "../../../../../components/AIChat/DraftFlowProvider";
import { useDraftFlowStore } from "../../../../../stores/draftFlowStore";
import { groupSessions } from "../../../../../utils/sessionUtils";
import { Skeleton } from "../../../../../components/shared";
import { SessionItem } from "./SessionItem";

export const SessionList = memo(({ onSelect }) => {
  const { data, loading } = useDraftFlowData();
  const { handleSaveRename, handleStartRename, handleArchiveSession } =
    useDraftFlowActions();
  const {
    selectedProjectId,
    activeSessionId,
    editingSessionId,
    editingTitle,
    setEditingTitle,
    setEditingSessionId,
  } = useDraftFlowStore();
  const { sessions, projectOptions, projectsById } = data;
  const { sessionsLoading } = loading;

  const projectNameById = useMemo(
    () => new Map(projectOptions.map((p) => [String(p?.id), p?.name])),
    [projectOptions],
  );

  const hasProjectsLoaded = Boolean(
    projectOptions.length || Object.keys(projectsById || {}).length,
  );

  const filteredSessions = useMemo(() => {
    if (!selectedProjectId) return sessions;
    return sessions.filter(
      (session) => String(session?.project_id) === String(selectedProjectId),
    );
  }, [sessions, selectedProjectId]);

  const sessionGroups = useMemo(() => {
    if (selectedProjectId) {
      return groupSessions(filteredSessions);
    }
    return filteredSessions.reduce((acc, session) => {
      const projectKey = String(session?.project_id || "");
      const projectName =
        projectNameById.get(projectKey) ||
        projectsById?.[projectKey]?.title ||
        projectsById?.[projectKey]?.name ||
        "General";
      if (!acc[projectName]) acc[projectName] = [];
      acc[projectName].push(session);
      return acc;
    }, {});
  }, [filteredSessions, selectedProjectId, projectNameById, projectsById]);

  if (sessionsLoading) {
    return (
      <div className="space-y-0.5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="p-2 flex gap-2 items-center">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-3 w-3/4 rounded" />
              <Skeleton className="h-2 w-1/2 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (filteredSessions.length === 0) {
    // No inline empty message: the chat canvas owns the single empty state,
    // and the panel header already offers the New Chat CTA.
    return null;
  }

  return (
    <div className="space-y-4">
      {Object.entries(sessionGroups).map(([label, groupedSessions]) => (
        <div key={label}>
          <div className="px-2 py-1.5 text-[10px] font-semibold text-mediumgrey uppercase tracking-wider sticky top-0 bg-white/95 backdrop-blur-sm z-10">
            {label}
          </div>
          <div className="space-y-0.5">
            {groupedSessions.map((session) => (
              <SessionItem
                key={session?.id}
                session={session}
                isActive={String(activeSessionId) === String(session?.id)}
                editingSessionId={editingSessionId}
                editingTitle={editingTitle}
                onSelect={onSelect}
                onSetEditingTitle={setEditingTitle}
                onSaveRename={handleSaveRename}
                onStartRename={handleStartRename}
                onCancelEdit={() => setEditingSessionId(null)}
                onArchive={handleArchiveSession}
                showProjectLabel={!selectedProjectId}
                projectName={
                  projectNameById.get(String(session?.project_id)) ||
                  projectsById?.[String(session?.project_id)]?.title ||
                  projectsById?.[String(session?.project_id)]?.name
                }
                projectNameFallback={
                  hasProjectsLoaded ? null : "Loading project"
                }
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

SessionList.displayName = "SessionList";
